// src/components/features/Interior/components/InteriorStep2DesignTheme.jsx
// Step 2: Design Theme — color/style swipe cards with image thumbnails

import {
  Box, Flex, Text, Button, VStack, HStack, Badge, IconButton,
} from "@chakra-ui/react";
import { useState, useRef } from "react";

// TODO: Replace with actual interior theme images
const THEMES = [
  { id: "modern",        label: "Modern",           color: "#E8F4F8", tags: ["Modern", "Minimalist", "White & Grey"],  img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=500&q=75" },
  { id: "contemporary",  label: "Contemporary",     color: "#F0EBE3", tags: ["Contemporary", "Warm", "Beige"],         img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=500&q=75" },
  { id: "traditional",   label: "Traditional",      color: "#F5EDD6", tags: ["Traditional", "Classic", "Wooden"],     img: "https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=500&q=75" },
  { id: "industrial",    label: "Industrial",       color: "#E8E0D8", tags: ["Industrial", "Raw", "Concrete"],        img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829e1?w=500&q=75" },
  { id: "scandinavian",  label: "Scandinavian",     color: "#F4F6F0", tags: ["Scandinavian", "Minimal", "Natural"],   img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=500&q=75" },
  { id: "bohemian",      label: "Bohemian",         color: "#F2E8DC", tags: ["Bohemian", "Eclectic", "Colorful"],     img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=500&q=75" },
  { id: "mid_century",   label: "Mid-Century",      color: "#EDE8E0", tags: ["Mid-Century", "Retro", "Organic"],     img: "https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=500&q=75" },
  { id: "coastal",       label: "Coastal",          color: "#E0EEF4", tags: ["Coastal", "Blue & White", "Breezy"],   img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829e1?w=500&q=75" },
  { id: "art_deco",      label: "Art Deco",         color: "#EDE0D4", tags: ["Art Deco", "Luxurious", "Gold"],       img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=500&q=75" },
  { id: "japandi",       label: "Japandi",          color: "#EEECE6", tags: ["Japandi", "Calm", "Neutral"],          img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=500&q=75" },
  { id: "tropical",      label: "Tropical",         color: "#E8F0E8", tags: ["Tropical", "Green", "Natural"],        img: "https://images.unsplash.com/photo-1560185127-6ed189bf02f4?w=500&q=75" },
  { id: "mediterranean", label: "Mediterranean",    color: "#F0EAE0", tags: ["Mediterranean", "Earthy", "Arches"],  img: "https://images.unsplash.com/photo-1593359677879-a4bb92f829e1?w=500&q=75" },
];

// Color palette options
const COLOR_PALETTES = [
  { id: "white_grey",   label: "White & Grey",     hex: ["#FFFFFF", "#9CA3AF", "#4B5563"] },
  { id: "warm_beige",   label: "Warm Beige",        hex: ["#F5F0E8", "#C4A882", "#8B6B47"] },
  { id: "navy_gold",    label: "Navy & Gold",       hex: ["#1E3A5F", "#D4AF37", "#FFFFFF"] },
  { id: "green_wood",   label: "Green & Wood",      hex: ["#4A7C59", "#8B6B47", "#F5F0E8"] },
  { id: "blush_rose",   label: "Blush & Rose",      hex: ["#F4C2C2", "#C9748C", "#FAF0F0"] },
  { id: "charcoal",     label: "Charcoal & White",  hex: ["#374151", "#9CA3AF", "#FFFFFF"] },
  { id: "terracotta",   label: "Terracotta",        hex: ["#E07B54", "#C4613A", "#F5EDD6"] },
  { id: "black_white",  label: "Black & White",     hex: ["#111827", "#FFFFFF", "#6B7280"] },
];

function SwipeCard({ theme, onLike, onDislike, current, total }) {
  const startX  = useRef(null);
  const [drag,  setDrag]  = useState(0);
  const [going, setGoing] = useState(null);
  const THRESHOLD = 70;
  const getX = (e) => (e.touches ? e.touches[0]?.clientX : e.clientX);
  const onStart = (e) => { startX.current = getX(e); };
  const onMove  = (e) => { if (startX.current === null) return; setDrag(getX(e) - startX.current); };
  const onEnd   = () => {
    if (drag > THRESHOLD)       { setGoing("right"); setTimeout(() => { setDrag(0); setGoing(null); onLike(); }, 260); }
    else if (drag < -THRESHOLD) { setGoing("left");  setTimeout(() => { setDrag(0); setGoing(null); onDislike(); }, 260); }
    else setDrag(0);
    startX.current = null;
  };
  const tx  = going === "left" ? -420 : going === "right" ? 420 : drag;
  const rot = Math.min(Math.max(drag / 14, -14), 14);

  return (
    <Box borderRadius="12px" overflow="hidden" border="1px solid #E2E8F0" boxShadow="md"
      cursor="grab" userSelect="none"
      style={{ transform: `translateX(${tx}px) rotate(${rot}deg)`, opacity: going ? 0 : 1, transition: going ? "all 0.26s ease" : "transform 0.05s linear" }}
      onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
    >
      <Box h="180px" bgImage={`url(${theme.img})`} bgSize="cover" bgPos="center" position="relative">
        {drag < -25 && <Box position="absolute" top={3} left={3} bg="red.500" px={2} py={1} borderRadius="6px"><Text fontSize="11px" fontWeight="700" color="white">✕ Skip</Text></Box>}
        {drag > 25  && <Box position="absolute" top={3} right={3} bg="green.500" px={2} py={1} borderRadius="6px"><Text fontSize="11px" fontWeight="700" color="white">✓ Like</Text></Box>}
        <Box position="absolute" top={2} right={2} bg="blackAlpha.600" px={2} py={0.5} borderRadius="20px">
          <Text fontSize="10px" color="white" fontWeight="600">{current} / {total}</Text>
        </Box>
        <Flex position="absolute" bottom={0} left={0} right={0} justify="space-between" align="center" px={3} py={2} bgGradient="linear(to-t, blackAlpha.700, transparent)">
          <Flex align="center" gap={1}>
            <Box w="18px" h="18px" borderRadius="full" bg="red.400" display="flex" alignItems="center" justifyContent="center"><Text fontSize="9px" color="white" fontWeight="800">✕</Text></Box>
            <Text fontSize="9px" color="whiteAlpha.900">Swipe left if you Don't like</Text>
          </Flex>
          <Flex align="center" gap={1}>
            <Text fontSize="9px" color="whiteAlpha.900">Swipe Right if you like</Text>
            <Box w="18px" h="18px" borderRadius="full" bg="green.400" display="flex" alignItems="center" justifyContent="center"><Text fontSize="9px" color="white" fontWeight="800">✓</Text></Box>
          </Flex>
        </Flex>
      </Box>
      <Box bg={theme.color} px={3} py={2}>
        <HStack spacing={1} flexWrap="wrap">
          {theme.tags.map((tag) => (
            <Badge key={tag} colorScheme="cyan" variant="subtle" fontSize="10px" px={2} py={0.5} borderRadius="5px">{tag}</Badge>
          ))}
        </HStack>
      </Box>
    </Box>
  );
}

export default function InteriorStep2DesignTheme({
  formData, setFormData, onNext, onBack, isLastStep, onSubmit, viewMode,
}) {
  const [cardIndex, setCardIndex] = useState(0);
  const [likedIds,  setLikedIds]  = useState(formData?.interiorLikedThemes || []);
  const selectedThemeId  = formData?.interiorTheme  || "";
  const selectedColorId  = formData?.interiorColor  || "";

  const currentTheme = THEMES[cardIndex] ?? null;
  const allSwiped    = cardIndex >= THEMES.length;

  const handleLike = () => {
    const id = THEMES[cardIndex]?.id;
    if (!id) return;
    const newLiked = [...likedIds, id];
    setLikedIds(newLiked);
    setFormData({ ...formData, interiorTheme: id, interiorLikedThemes: newLiked });
    setCardIndex((i) => i + 1);
  };
  const handleDislike = () => setCardIndex((i) => i + 1);
  const handleThumbSelect = (id) => setFormData({ ...formData, interiorTheme: id });
  const handleColorSelect = (id) => setFormData({ ...formData, interiorColor: id });

  const canProceed = !!selectedThemeId && !!selectedColorId;
  const handleNext = () => {
    if (!canProceed) return;
    if (isLastStep) onSubmit(); else onNext();
  };

  const selectedTheme = THEMES.find((t) => t.id === selectedThemeId);

  return (
    <Box h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between" px={4} py={6}>
      <Box flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        pointerEvents={viewMode ? "none" : "auto"}
        opacity={viewMode ? 0.6 : 1}
        css={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" } }}>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="17px" fontWeight="600" color="gray.800">Design Theme</Text>
            <Text fontSize="12px" color="gray.500" mt={0.5}>Select your preferred interior style and color palette</Text>
          </Box>

          {/* Swipe card */}
          {!allSwiped ? (
            <SwipeCard key={cardIndex} theme={currentTheme} onLike={handleLike} onDislike={handleDislike} current={cardIndex + 1} total={THEMES.length} />
          ) : (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="10px" px={3} py={2} textAlign="center">
              <Text fontSize="13px" color="green.700" fontWeight="600">✓ All themes reviewed</Text>
              <Text fontSize="11px" color="green.600">Select from thumbnails below</Text>
            </Box>
          )}

          {/* Like/Dislike buttons */}
          {!allSwiped && (
            <Flex gap={3} justify="center" align="center">
              <IconButton onClick={handleDislike} aria-label="Dislike" borderRadius="full" w="44px" h="44px"
                bg="red.50" color="red.500" border="2px solid" borderColor="red.200" _hover={{ bg: "red.100" }}
                icon={<Text fontSize="16px" lineHeight="1">✕</Text>} />
              <Text fontSize="11px" color="gray.400" fontWeight="500">{cardIndex + 1} / {THEMES.length}</Text>
              <IconButton onClick={handleLike} aria-label="Like" borderRadius="full" w="44px" h="44px"
                bg="green.50" color="green.500" border="2px solid" borderColor="green.200" _hover={{ bg: "green.100" }}
                icon={<Text fontSize="16px" lineHeight="1">✓</Text>} />
            </Flex>
          )}

          {/* Theme thumbnails */}
          <Box>
            <Text fontSize="11px" color="gray.500" mb={2}>
              {allSwiped ? "Tap to select theme" : "Or tap to select directly"}
            </Text>
            <Flex gap="6px" flexWrap="wrap">
              {THEMES.map((t) => {
                const isSel = selectedThemeId === t.id;
                const isLiked = likedIds.includes(t.id);
                return (
                  <Box key={t.id} position="relative" w="54px" h="42px" borderRadius="7px" overflow="hidden"
                    border="2px solid" borderColor={isSel ? "cyan.500" : isLiked ? "green.300" : "#E2E8F0"}
                    cursor="pointer" flexShrink={0} onClick={() => handleThumbSelect(t.id)}
                    transition="0.15s" _hover={{ borderColor: "cyan.300" }}>
                    <Box w="100%" h="100%" bgImage={`url(${t.img})`} bgSize="cover" bgPos="center" />
                    {isSel && (
                      <>
                        <Box position="absolute" inset={0} bg="cyan.500" opacity={0.4} />
                        <Flex position="absolute" inset={0} align="center" justify="center">
                          <Text fontSize="13px" color="white" fontWeight="800">✓</Text>
                        </Flex>
                      </>
                    )}
                    {isLiked && !isSel && <Box position="absolute" top="2px" right="2px" w="7px" h="7px" borderRadius="full" bg="green.400" />}
                  </Box>
                );
              })}
            </Flex>
          </Box>

          {/* Selected theme */}
          {selectedTheme && (
            <Box border="2px solid #319795" borderRadius="10px" bg="cyan.50" px={3} py={2}>
              <Text fontSize="12px" fontWeight="700" color="cyan.700">✓ Theme: {selectedTheme.label}</Text>
              <HStack mt={0.5} spacing={1} flexWrap="wrap">
                {selectedTheme.tags.map((t) => (
                  <Badge key={t} colorScheme="cyan" variant="subtle" fontSize="9px" px={1.5} borderRadius="4px">{t}</Badge>
                ))}
              </HStack>
            </Box>
          )}

          {/* Color Palette */}
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.700" mb={2}>Color Preference</Text>
            <Flex gap="8px" flexWrap="wrap">
              {COLOR_PALETTES.map((p) => {
                const isSel = selectedColorId === p.id;
                return (
                  <Box key={p.id} cursor="pointer" onClick={() => handleColorSelect(p.id)}
                    border="2px solid" borderColor={isSel ? "cyan.500" : "#E2E8F0"}
                    borderRadius="8px" p={1.5} bg={isSel ? "cyan.50" : "white"}
                    transition="0.15s" _hover={{ borderColor: "cyan.300" }} minW="70px">
                    <HStack spacing={1} justify="center" mb={1}>
                      {p.hex.map((c, i) => (
                        <Box key={i} w="12px" h="12px" borderRadius="full" bg={c} border="1px solid #E2E8F0" />
                      ))}
                    </HStack>
                    <Text fontSize="9px" color="gray.600" textAlign="center" lineHeight="1.2">{p.label}</Text>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px"
          minW="100px" h="40px" fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext} isDisabled={!canProceed} variant="solid"
          bg={canProceed ? "cyan.500" : "gray.300"} color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: canProceed ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}